/**
 * Party ledger page.
 *
 * Columns: Date | Description | Dr | Cr | Balance | Attach | Source.
 * Actions: Record payment, Record adjustment, Download PDF, Download PNG.
 * Toggle: show reversed entries.
 * Attachment viewer: click thumbnail → full-screen viewer.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import type {
  Adjustment,
  EntryAttachment,
  LedgerListing,
  LedgerListingRow,
  Party,
  PartyBalance,
  Payment,
} from '../api';
import {
  ArrowDownToLine,
  BookOpen,
  Copy,
  FileText,
  ImageIcon,
  Paperclip,
  Pencil,
  PencilRuler,
  RotateCcw,
  Share2,
  Undo2,
} from 'lucide-react';
import { AppShell } from '../ui/AppShell';
import { useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { StickySummaryBar } from '../ui/mobileOps';
import { Btn, Card, SectionCard, StatePill } from '../ui/components';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorBanner,
  OverflowMenu,
  Spinner,
} from '../ui/primitives';
import { PaymentDialog } from '../payments/PaymentDialog';
import { AdjustmentDialog } from '../adjustments/AdjustmentDialog';
import { EditPartyDialog } from '../parties/EditPartyDialog';
import { TransactionDetailModal } from '../ledger/detail/TransactionDetailModal';
import { VoidRedoDialog, type VoidRedoTarget } from '../ledger/VoidRedoDialog';
import { kinrToInrString } from '../money/inr';
import { ShareLedgerDialog } from '../ledger/ShareLedgerDialog';
import { buildShareCaption } from '../ledger/shareCaption';
import { withCacheBust } from '../ledger/exportUrl';
import { EntityManageMenu } from '../components/EntityManageMenu';
import { usePinGate } from '../auth/pinGate';
import { uploadFile } from '../lib/uploadFile';

export function PartyLedgerPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const partyId = Number(idParam);
  const qc = useQueryClient();
  const nav = useNavigate();
  const readOnly = useReadOnly();

  const today = useMemo(() => todayIso(), []);

  const [from, setFrom] = useState(addDaysIso(today, -30));
  const [to, setTo] = useState(today);

  const openingBalanceQuery = useQuery<import('../api').OpeningBalance | null>({
    queryKey: ['opening-balance', partyId, today],
    queryFn: () =>
      api.get<import('../api').OpeningBalance | null>(
        `/api/opening-balances/${partyId}?asOf=${today}`,
      ).catch(() => null),
    enabled: Number.isFinite(partyId),
  });
  useEffect(() => {
    const ob = openingBalanceQuery.data;
    if (!ob) return;
    const smartFrom = addDaysIso(ob.asOfDate, 1);
    setFrom((prev) => (smartFrom > prev ? smartFrom : prev));
  }, [openingBalanceQuery.data]);

  const [includeReversed, setIncludeReversed] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [editPartyOpen, setEditPartyOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [viewer, setViewer] = useState<{ entryId: number; index: number } | null>(null);
  const [detailEntryId, setDetailEntryId] = useState<number | null>(null);
  // Void & Redo (corrections Phase 3a): the confirm dialog target, plus the
  // record to prefill the create dialog with after a redo-flavoured void.
  const [voidTarget, setVoidTarget] = useState<VoidRedoTarget | null>(null);
  const [redoPayment, setRedoPayment] = useState<Payment | null>(null);
  const [redoAdjustment, setRedoAdjustment] = useState<Adjustment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Modals (transaction detail / payment / adjustment) are driven by URL
  // search params so the device Back button closes the open modal and keeps
  // you on THIS client's ledger instead of leaving for /ledgers.
  const openModal = (key: string, val = '1') =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(key, val);
      return next;
    });
  const closeModal = () => nav(-1);

  useEffect(() => {
    // `focus_entry` is the legacy "Open other side" deep-link; `tx` is the
    // in-page open. Either drives the detail modal.
    const focus = searchParams.get('focus_entry') ?? searchParams.get('tx');
    const id = focus ? parseInt(focus, 10) : NaN;
    setDetailEntryId(Number.isFinite(id) ? id : null);
    setPaymentOpen(searchParams.get('pay') === '1');
    setAdjustmentOpen(searchParams.get('adj') === '1');
  }, [searchParams]);

  const closeDetail = closeModal;

  const party = useQuery<Party>({
    queryKey: ['party', partyId],
    queryFn: () => api.get<Party>(`/api/parties/${partyId}`),
    enabled: Number.isFinite(partyId),
  });
  const balance = useQuery<PartyBalance>({
    queryKey: ['balance', partyId],
    queryFn: () => api.get<PartyBalance>(`/api/parties/${partyId}/balance`),
    enabled: Number.isFinite(partyId),
  });
  const ledger = useQuery<LedgerListing>({
    queryKey: ['ledger', partyId, from, to, includeReversed],
    queryFn: () =>
      api.get<LedgerListing>(
        `/api/parties/${partyId}/ledger?from=${from}&to=${to}&include_reversed=${
          includeReversed ? 'true' : 'false'
        }`,
      ),
    enabled: Number.isFinite(partyId) && !!from && !!to,
  });

  const attachmentQueries = useQueries({
    queries: (ledger.data?.rows ?? []).map((r) => ({
      queryKey: ['entry-attachments', r.id],
      queryFn: () => api.get<EntryAttachment[]>(`/api/ledger-entries/${r.id}/attachments`),
    })),
  });
  const attachmentsByEntry = useMemo(() => {
    const m = new Map<number, EntryAttachment[]>();
    (ledger.data?.rows ?? []).forEach((r, i) => {
      const data = attachmentQueries[i]?.data;
      if (data) m.set(r.id, data);
    });
    return m;
  }, [ledger.data, attachmentQueries]);

  const reverseEntryMut = useMutation({
    mutationFn: async ({ entryId, reason }: { entryId: number; reason: string }) =>
      api.post(`/api/ledger-entries/${entryId}/reverse`, { reason }),
    onSuccess: () => void qc.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const uploadAttachmentMut = useMutation({
    mutationFn: ({ entryId, file }: { entryId: number; file: File }) =>
      uploadFile<{ attachment: { id: number } }>(
        `/api/ledger-entries/${entryId}/attachments`,
        file,
      ),
    onSuccess: (_, { entryId }) => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments', entryId] });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : String(err)),
  });

  const deleteAttachmentMut = useMutation({
    mutationFn: (attachmentId: number) =>
      api.delete(`/api/entry-attachments/${attachmentId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['entry-attachments'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const { withPin } = usePinGate();

  useEffect(() => {
    setError(null);
  }, [partyId]);

  if (!Number.isFinite(partyId)) {
    return (
      <AppShell title="Bad party id">
        <Card>
          <ErrorBanner>Not a valid party id.</ErrorBanner>
        </Card>
      </AppShell>
    );
  }

  const partyName = party.data?.displayName ?? '…';
  const currentBalance = balance.data?.balance ?? '…';
  // Shareable one-liner derived from the current balance: "₹X Total due" /
  // "₹X Aapke" / "Settled".
  const balanceCaption = balance.data ? buildShareCaption(balance.data.balance) : null;
  async function copyBalanceCaption(): Promise<void> {
    if (!balanceCaption) return;
    try {
      await navigator.clipboard.writeText(balanceCaption);
      setCaptionCopied(true);
      window.setTimeout(() => setCaptionCopied(false), 2000);
    } catch {
      /* clipboard unavailable (non-secure context) — ignore */
    }
  }

  const pdfUrl = withCacheBust(`/api/parties/${partyId}/ledger.pdf?from=${from}&to=${to}&include_reversed=${includeReversed}`);
  const pngUrl = withCacheBust(`/api/parties/${partyId}/ledger.png?from=${from}&to=${to}&include_reversed=${includeReversed}`);
  const pngFileName = `ledger-${partyId}-${from}-${to}.png`;

  return (
    <AppShell
      title={`Ledger — ${partyName}`}
      toolbar={
        <PageHeader
          section="ledgers"
          title={partyName}
          breadcrumb={[{ label: 'Ledgers', to: '/ledgers' }]}
          actions={
            <>
              {!readOnly && (
                <>
                  <Btn size="sm" className="hidden md:inline-flex" onClick={() => openModal('pay')}>
                    <ArrowDownToLine className="h-3.5 w-3.5" strokeWidth={2} />
                    <span className="hidden sm:inline">Payment</span>
                  </Btn>
                  <Btn size="sm" variant="secondary" className="hidden md:inline-flex" onClick={() => openModal('adj')}>
                    <PencilRuler className="h-3.5 w-3.5" strokeWidth={2} />
                    <span className="hidden sm:inline">Adjustment</span>
                  </Btn>
                </>
              )}
              <OverflowMenu
                items={[
                  {
                    label: 'Edit party…',
                    icon: Pencil,
                    onClick: () => setEditPartyOpen(true),
                    disabled: !party.data || readOnly,
                  },
                  { separator: true, label: '' },
                  {
                    label: 'Download PDF',
                    icon: FileText,
                    href: pdfUrl,
                    target: '_blank',
                  },
                  {
                    label: 'Download PNG',
                    icon: ImageIcon,
                    href: pngUrl,
                    download: pngFileName,
                  },
                  {
                    label: 'Share…',
                    icon: Share2,
                    onClick: () => setShareDialogOpen(true),
                  },
                ]}
              />
            </>
          }
        />
      }
    >
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-4">
          {error && <ErrorBanner>{error}</ErrorBanner>}

          <div className="md:hidden">
            <StickySummaryBar
              section="ledgers"
              title={partyName}
              subtitle="Open the running balance, then jump into the exact entry when you need context."
              metrics={[
                {
                  label: 'Balance',
                  value: currentBalance,
                  tone: currentBalance.startsWith('-') ? 'neg' : currentBalance === '0' ? 'default' : 'pos',
                },
                { label: 'Rows', value: String(ledger.data?.rows.length ?? 0) },
                { label: 'View', value: includeReversed ? 'All' : 'Live' },
              ]}
              actions={
                !readOnly ? (
                  <>
                    <Btn size="sm" onClick={() => openModal('pay')}>
                      Payment
                    </Btn>
                    <Btn size="sm" variant="secondary" onClick={() => openModal('adj')}>
                      Adjustment
                    </Btn>
                  </>
                ) : undefined
              }
            />
          </div>

          {/* Date range + toggle */}
          <SectionCard section="ledgers" title="Date range">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-small text-ink-muted">
              <label className="flex items-center gap-1.5">
                <span className="shrink-0 text-ink-muted">From:</span>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </label>
              <label className="flex items-center gap-1.5">
                <span className="shrink-0 text-ink-muted">To:</span>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="min-h-[36px] rounded-md border border-border bg-surface px-2 py-1.5 text-small text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                />
              </label>
              <Checkbox
                label="Show reversed entries"
                checked={includeReversed}
                onChange={setIncludeReversed}
              />
            </div>
          </SectionCard>

          {/* Ledger table */}
          <SectionCard section="ledgers" padded={false}>
            {ledger.isLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 text-small text-ink-muted">
                <Spinner /> loading…
              </div>
            ) : ledger.data ? (
              <>
                {/* Desktop: full ledger table */}
                <div className="hidden overflow-x-auto md:block">
                  <table className="min-w-full text-small">
                    <thead className="bg-surface-raised text-caption uppercase tracking-wide text-ink-muted">
                      <tr>
                        <th className="px-4 py-2.5 text-left">Date</th>
                        <th className="px-4 py-2.5 text-left">Description</th>
                        <th className="px-4 py-2.5 text-right">Dr.</th>
                        <th className="px-4 py-2.5 text-right">Cr.</th>
                        <th className="px-4 py-2.5 text-right">Balance</th>
                        <th className="px-4 py-2.5 text-left">Source</th>
                        <th className="px-4 py-2.5 text-left">Attach</th>
                        <th className="px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {/* Opening balance row */}
                      <tr className="bg-sec-ledgers-tint/50">
                        <td className="px-4 py-2.5 text-sec-ledgers">{from}</td>
                        <td className="px-4 py-2.5 font-medium text-sec-ledgers">Opening balance</td>
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-sec-ledgers">
                          {ledger.data.openingBalanceAsOfFrom}
                        </td>
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5" />
                      </tr>
                      {ledger.data.rows.map((row) => (
                        <LedgerRowView
                          key={row.id}
                          row={row}
                          attachments={attachmentsByEntry.get(row.id) ?? []}
                          readOnly={readOnly}
                          onOpenDetail={() => openModal('tx', String(row.id))}
                          onOpenAttachment={(entryId) =>
                            setViewer({ entryId, index: 0 })
                          }
                          onAttachFile={(entryId, file) =>
                            uploadAttachmentMut.mutate({ entryId, file })
                          }
                          uploadingEntryId={
                            uploadAttachmentMut.isPending
                              ? uploadAttachmentMut.variables?.entryId ?? null
                              : null
                          }
                          onReverse={(entryId, reason) =>
                            void withPin(() =>
                              reverseEntryMut.mutateAsync({ entryId, reason }),
                            )
                          }
                          onVoidAction={setVoidTarget}
                        />
                      ))}
                      {ledger.data.rows.length === 0 && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 text-center text-small text-ink-muted">
                            No entries in this range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border bg-surface-raised">
                        <td className="px-4 py-2.5 font-semibold text-ink" colSpan={4}>
                          Closing balance ({to})
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-ink">
                          {ledger.data.closingBalance}
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Mobile: ledger entry cards */}
                <div className="space-y-3 p-3 md:hidden">
                  {/* Opening balance card */}
                  <div className="rounded-md border border-sec-ledgers/30 bg-sec-ledgers-tint/50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-small font-semibold text-sec-ledgers">Opening balance</span>
                      <span className="font-mono font-semibold text-sec-ledgers">
                        {ledger.data.openingBalanceAsOfFrom}
                      </span>
                    </div>
                    <p className="mt-0.5 text-caption text-sec-ledgers/70">{from}</p>
                  </div>

                  {/* Entry cards */}
                  {ledger.data.rows.map((row) => (
                    <LedgerEntryMobileCard
                      key={row.id}
                      row={row}
                      attachmentCount={attachmentsByEntry.get(row.id)?.length ?? 0}
                      onOpenDetail={() => openModal('tx', String(row.id))}
                      onAttach={(file) => uploadAttachmentMut.mutate({ entryId: row.id, file })}
                      uploading={
                        uploadAttachmentMut.isPending &&
                        uploadAttachmentMut.variables?.entryId === row.id
                      }
                      readOnly={readOnly}
                    />
                  ))}

                  {ledger.data.rows.length === 0 && (
                    <p className="py-4 text-center text-small text-ink-muted">
                      No entries in this range.
                    </p>
                  )}

                  {/* Closing balance card */}
                  <div className="rounded-md border border-border bg-surface-raised p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-small font-semibold text-ink">
                        Closing balance ({to})
                      </span>
                      <span className="font-mono font-semibold text-ink">
                        {ledger.data.closingBalance}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-3">
                <EmptyState
                  section="ledgers"
                  icon={BookOpen}
                  title="No ledger data"
                  description="Adjust the date range above."
                />
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Sidebar ────────────────────────────────────────────────────── */}
        <aside className="space-y-4">
          <SectionCard section="ledgers" title="Current balance" className="hidden lg:block">
            <p className={`font-mono text-display font-bold tabular-nums ${toneFromBalance(currentBalance)}`}>
              {currentBalance}
            </p>
            {balanceCaption && (
              <button
                type="button"
                onClick={() => void copyBalanceCaption()}
                title="Copy a shareable one-liner of the current balance"
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
              >
                {captionCopied ? (
                  <span className="text-pos">Copied “{balanceCaption}”</span>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                    Copy “{balanceCaption}”
                  </>
                )}
              </button>
            )}
            <p className="mt-1 text-caption text-ink-muted">
              K-INR. Positive = party owes you; negative = you owe party.
            </p>
          </SectionCard>
          <SectionCard section="ledgers" title="Role">
            <div className="flex items-center gap-2">
              <StatePill state={party.data?.role ?? '—'} />
              {party.data?.status === 'archived' && <StatePill state="archived" />}
            </div>
          </SectionCard>
          {!readOnly && party.data && party.data.role !== 'self' && (
            <SectionCard section="ledgers" title="Manage">
              <p className="mb-2 text-caption text-ink-muted">
                Archive hides this {party.data.role} from your active lists but keeps all history.
                Delete is only possible when nothing references it.
              </p>
              <EntityManageMenu
                kind="parties"
                id={partyId}
                noun={party.data.role}
                isArchived={party.data.status === 'archived'}
                archivePinGated
                onChanged={() => {
                  void qc.invalidateQueries({ queryKey: ['party', partyId] });
                  void qc.invalidateQueries({ queryKey: ['parties'] });
                }}
                onDeleted={() => nav('/ledgers', { replace: true })}
              />
            </SectionCard>
          )}
        </aside>
      </div>

      {paymentOpen && (
        <PaymentDialog
          preselectPayeeId={partyId}
          onClose={closeModal}
          onCreated={closeModal}
        />
      )}

      {adjustmentOpen && (
        <AdjustmentDialog
          preselectDrPartyId={partyId}
          onClose={closeModal}
          onCreated={closeModal}
        />
      )}

      {editPartyOpen && party.data && (
        <EditPartyDialog
          party={party.data}
          onClose={() => setEditPartyOpen(false)}
          onSaved={() => setEditPartyOpen(false)}
        />
      )}

      <ShareLedgerDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        partyId={partyId}
        partyName={party.data?.displayName ?? `party-${partyId}`}
        from={from}
        to={to}
        includeReversed={includeReversed}
      />


      {detailEntryId !== null && (
        <TransactionDetailModal
          entryId={detailEntryId}
          currentPartyId={partyId}
          onClose={closeDetail}
          onVoidAction={(target) => {
            closeDetail(); // pop the ?tx= param; the dialog is plain state
            setVoidTarget(target);
          }}
        />
      )}

      {voidTarget && (
        <VoidRedoDialog
          target={voidTarget}
          onClose={() => setVoidTarget(null)}
          onVoided={({ payment, adjustment }) => {
            const mode = voidTarget.mode;
            setVoidTarget(null);
            if (mode === 'redo') {
              if (payment) setRedoPayment(payment);
              if (adjustment) setRedoAdjustment(adjustment);
            }
          }}
        />
      )}

      {redoPayment && (
        <PaymentDialog
          initial={{
            payerPartyId: redoPayment.payerPartyId,
            payeePartyId: redoPayment.payeePartyId,
            amountInr: kinrToInrString(redoPayment.amount),
            method: redoPayment.method,
            viaSelf: redoPayment.viaSelf,
            effectiveDate: redoPayment.effectiveDate,
            note: redoPayment.note ?? '',
          }}
          onClose={() => setRedoPayment(null)}
          onCreated={() => setRedoPayment(null)}
        />
      )}

      {redoAdjustment && (
        <AdjustmentDialog
          initial={{
            drPartyId: redoAdjustment.drPartyId,
            crPartyId: redoAdjustment.crPartyId,
            amountInr: kinrToInrString(redoAdjustment.amount),
            description: redoAdjustment.description,
            note: redoAdjustment.note ?? '',
            effectiveDate: redoAdjustment.effectiveDate,
          }}
          onClose={() => setRedoAdjustment(null)}
          onCreated={() => setRedoAdjustment(null)}
        />
      )}

      {viewer && (
        <AttachmentViewer
          entryId={viewer.entryId}
          attachments={attachmentsByEntry.get(viewer.entryId) ?? []}
          index={viewer.index}
          readOnly={readOnly}
          onIndexChange={(index) => setViewer({ ...viewer, index })}
          onClose={() => setViewer(null)}
          onAddFile={(entryId, file) =>
            uploadAttachmentMut.mutate({ entryId, file })
          }
          onDeleteAttachment={(attachmentId) => {
            const list = attachmentsByEntry.get(viewer.entryId) ?? [];
            deleteAttachmentMut.mutate(attachmentId);
            if (list.length <= 1) {
              setViewer(null);
            } else {
              setViewer({
                entryId: viewer.entryId,
                index: Math.max(0, viewer.index - (viewer.index === list.length - 1 ? 1 : 0)),
              });
            }
          }}
        />
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function LedgerEntryMobileCard({
  row,
  attachmentCount,
  onOpenDetail,
  onAttach,
  uploading,
  readOnly,
}: {
  row: LedgerListingRow;
  attachmentCount: number;
  onOpenDetail: () => void;
  onAttach: (file: File) => void;
  uploading: boolean;
  readOnly: boolean;
}) {
  const balanceNum = parseFloat(String(row.runningBalance).replace(/,/g, ''));
  const balancePositive = !Number.isNaN(balanceNum) && balanceNum >= 0;
  const isReversal = row.reversesEntryId !== null;
  const hasDr = row.dr !== '0';
  const hasCr = row.cr !== '0';
  const source = sourceLabel(row);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenDetail}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpenDetail();
        }
      }}
      className={[
        'cursor-pointer overflow-hidden rounded-[22px] border border-border/70 app-shell-surface-soft p-4 shadow-sm backdrop-blur transition-colors active:bg-surface-raised',
        isReversal ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-small font-semibold tracking-[-0.01em] text-ink">{row.description}</div>
            {isReversal && <StatePill state="archived" />}
          </div>
          <div className="mt-1 text-[12px] text-ink-muted">{row.effectiveDate}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
            <span>{source}</span>
            {attachmentCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sec-ledgers-tint px-2 py-0.5 font-medium text-sec-ledgers">
                <Paperclip className="h-3 w-3" strokeWidth={2} />
                {attachmentCount}
              </span>
            )}
            {!readOnly && !isReversal && (
              <span onClick={(e) => e.stopPropagation()}>
                <AttachFileButton onFile={onAttach} uploading={uploading} disabled={false} />
              </span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`font-mono text-[1rem] font-semibold tabular-nums ${balancePositive ? 'text-ink' : 'text-neg'}`}>
            {row.runningBalance}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">running</div>
        </div>
      </div>
      {(hasDr || hasCr) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border/70 pt-3 text-caption">
          {hasDr && (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 font-medium text-ink ring-1 ring-border/60">
              <span className="text-ink-muted">Dr</span>
              <span className="font-mono">{row.dr}</span>
            </span>
          )}
          {hasCr && (
            <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2.5 py-1 font-medium text-warn ring-1 ring-border/60">
              <span>Cr</span>
              <span className="font-mono">{row.cr}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function LedgerRowView({
  row,
  attachments,
  readOnly,
  onOpenDetail,
  onOpenAttachment,
  onAttachFile,
  uploadingEntryId,
  onReverse,
  onVoidAction,
}: {
  row: LedgerListingRow;
  attachments: EntryAttachment[];
  readOnly: boolean;
  onOpenDetail: () => void;
  onOpenAttachment: (entryId: number) => void;
  onAttachFile: (entryId: number, file: File) => void;
  uploadingEntryId: number | null;
  onReverse: (entryId: number, reason: string) => void;
  onVoidAction: (target: VoidRedoTarget) => void;
}) {
  const source = sourceLabel(row);
  const isReversal = row.reversesEntryId !== null;
  const isUploading = uploadingEntryId === row.id;
  return (
    <tr
      onClick={onOpenDetail}
      className={`cursor-pointer transition-colors hover:bg-surface-raised ${
        isReversal ? 'text-ink-muted' : 'text-ink'
      }`}
    >
      <td className="px-4 py-2.5">{row.effectiveDate}</td>
      <td className="px-4 py-2.5">{row.description}</td>
      <td className="px-4 py-2.5 text-right font-mono">
        {row.dr === '0' ? '' : row.dr}
      </td>
      <td className="px-4 py-2.5 text-right font-mono text-warn">
        {row.cr === '0' ? '' : `-${row.cr}`}
      </td>
      <td className="px-4 py-2.5 text-right font-mono">{row.runningBalance}</td>
      <td className="px-4 py-2.5">
        <span className="inline-flex items-center rounded-full bg-surface-raised px-2 py-0.5 text-caption font-medium text-ink-muted">
          {source}
        </span>
      </td>
      <td className="px-4 py-2.5">
        {attachments.length > 0 ? (
          <button
            type="button"
            className="text-caption text-sec-ledgers hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onOpenAttachment(row.id);
            }}
          >
            {attachments.length} file{attachments.length > 1 ? 's' : ''}
          </button>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <AttachFileButton
              disabled={isReversal}
              uploading={isUploading}
              onFile={(file) => onAttachFile(row.id, file)}
            />
          </div>
        )}
      </td>
      <td className="px-4 py-2.5 text-right">
        {/* Payment / adjustment rows: fix-a-value actions via the shared
            void dialog (Void = plain; Void & re-enter = redo the entry
            prefilled). Reversal rows and posted-batch rows get nothing. */}
        {!readOnly && !isReversal && row.sourcePaymentId !== null && (
          <RowVoidActions
            onVoid={(mode) =>
              onVoidAction({ kind: 'payment', id: row.sourcePaymentId!, mode })
            }
          />
        )}
        {!readOnly && !isReversal && row.sourceAdjustmentId !== null && (
          <RowVoidActions
            onVoid={(mode) =>
              onVoidAction({ kind: 'adjustment', id: row.sourceAdjustmentId!, mode })
            }
          />
        )}
        {!readOnly &&
          !isReversal &&
          row.sourcePaymentId === null &&
          row.sourceAdjustmentId === null &&
          row.sourceSettlementId === null && (
            <button
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-neg transition-colors hover:bg-neg/5"
              onClick={(e) => {
                e.stopPropagation();
                const reason = prompt('Reason for reversing this entry?');
                if (reason && reason.trim().length > 0) {
                  onReverse(row.id, reason.trim());
                }
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} />
              Reverse
            </button>
          )}
      </td>
    </tr>
  );
}

/** Compact "Void / Void & re-enter" pair for a desktop ledger row. */
function RowVoidActions({ onVoid }: { onVoid: (mode: 'void' | 'redo') => void }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <button
        title="Void this entry, then re-enter it with corrected values"
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-sec-ledgers transition-colors hover:bg-sec-ledgers-tint"
        onClick={(e) => {
          e.stopPropagation();
          onVoid('redo');
        }}
      >
        <Pencil className="h-3.5 w-3.5" strokeWidth={2} />
        Fix
      </button>
      <button
        title="Void this entry (adds reversal entries; nothing is deleted)"
        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-neg transition-colors hover:bg-neg/5"
        onClick={(e) => {
          e.stopPropagation();
          onVoid('void');
        }}
      >
        <Undo2 className="h-3.5 w-3.5" strokeWidth={2} />
        Void
      </button>
    </span>
  );
}

function sourceLabel(row: LedgerListingRow): string {
  if (row.reversesEntryId !== null) return 'REVERSAL';
  if (row.sourceBatchId !== null) return 'BATCH';
  if (row.sourcePaymentId !== null) return 'PAYMENT';
  if (row.sourceAdjustmentId !== null) return 'ADJUSTMENT';
  return row.type;
}

function AttachFileButton({
  onFile,
  uploading,
  disabled,
}: {
  onFile: (file: File) => void;
  uploading: boolean;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  if (disabled) return <span className="text-caption text-ink-muted">—</span>;
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      <button
        type="button"
        disabled={uploading}
        title="Attach a receipt/image to this entry"
        onClick={() => inputRef.current?.click()}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-caption text-ink-muted transition-colors hover:border-sec-ledgers hover:bg-surface-raised hover:text-sec-ledgers disabled:cursor-wait disabled:opacity-60"
      >
        {uploading ? (
          <Spinner />
        ) : (
          <>
            <Paperclip className="h-3 w-3" strokeWidth={2} />
            <span>+ Add</span>
          </>
        )}
      </button>
    </>
  );
}

function AttachmentViewer({
  entryId,
  attachments,
  index,
  readOnly,
  onIndexChange,
  onClose,
  onAddFile,
  onDeleteAttachment,
}: {
  entryId: number;
  attachments: EntryAttachment[];
  index: number;
  readOnly: boolean;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onAddFile: (entryId: number, file: File) => void;
  onDeleteAttachment: (attachmentId: number) => void;
}) {
  const addFileInputRef = useRef<HTMLInputElement | null>(null);
  if (attachments.length === 0) return null;
  const idx = Math.max(0, Math.min(index, attachments.length - 1));
  const a = attachments[idx]!;
  const url = `/api/attachments/${a.sha256}`;
  const isImage = a.mimeType.startsWith('image/');
  const isPdf = a.mimeType === 'application/pdf';
  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-4xl flex-col items-center gap-3 rounded-card border border-border bg-surface p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex w-full items-center justify-between">
          <div className="text-small">
            <span className="font-medium text-ink">{a.originalFilename}</span>
            <span className="ml-2 text-caption text-ink-muted">
              {(a.byteSize / 1024).toFixed(0)} kB · {a.mimeType}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="max-h-[75vh] w-full overflow-auto rounded-md border border-border bg-surface-raised">
          {isImage ? (
            <img
              src={url}
              alt={a.originalFilename}
              className="mx-auto block max-h-[70vh] max-w-full"
            />
          ) : isPdf ? (
            <embed
              src={url}
              type="application/pdf"
              className="h-[70vh] w-full"
            />
          ) : (
            <div className="p-8 text-center text-small text-ink-muted">
              <p>Preview not supported for this file type.</p>
              <a
                href={url}
                download={a.originalFilename}
                className="mt-3 inline-block rounded-md border border-border px-3 py-2 text-ink hover:bg-surface-raised"
              >
                Download
              </a>
            </div>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {attachments.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onIndexChange(Math.max(0, idx - 1))}
                  disabled={idx === 0}
                >
                  ← Prev
                </Button>
                <span className="text-caption text-ink-muted">
                  {idx + 1} / {attachments.length}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => onIndexChange(Math.min(attachments.length - 1, idx + 1))}
                  disabled={idx === attachments.length - 1}
                >
                  Next →
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && (
              <>
                <input
                  ref={addFileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onAddFile(entryId, f);
                    if (addFileInputRef.current) addFileInputRef.current.value = '';
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => addFileInputRef.current?.click()}
                >
                  + Add more
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete "${a.originalFilename}"? This cannot be undone.`)) {
                      onDeleteAttachment(a.id);
                    }
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function toneFromBalance(v: string): string {
  if (v === '…' || v === '0') return 'text-ink-muted';
  if (v.startsWith('-')) return 'text-neg';
  return 'text-pos';
}

function todayIso(): string {
  const d = new Date();
  const y = d.getUTCFullYear().toString().padStart(4, '0');
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(isoDate: string, delta: number): string {
  const [y, m, d] = isoDate.split('-').map((s) => parseInt(s, 10));
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  date.setUTCDate(date.getUTCDate() + delta);
  const yy = date.getUTCFullYear().toString().padStart(4, '0');
  const mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const dd = date.getUTCDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
